import {
  getMessageFeed,
  getMsgInputForm,
  getMsgSendBtn,
  getUserMsgInputField,
  getHiddenMessages,
  getRequestId,
  getMessagesElement,
  getTestInformation,
  widgetIsClosed,
  setTimeStamp,
  getTimeStampElement,
  deleteTimestamp,
  setTimeStampIntervalID,
} from '@/components/chatbot/chatbotElementUtils';
import {
  hideLoadingDots,
  showLoadingDots,
} from '@/components/chatbot/chatbotLoadingDotsController';
import {
  removeButtons,
  setUpButtons,
  setUpNewUserForm,
  removeForms,
  autoScrollToBottom,
} from '@/components/chatbot/chatbotButtonsAndFormsController';
import { maximizeContainer } from '@/components/chatbot/chatbotMinMaxButtonsController';
import { checkForSessionTimeout } from '@/components/chatbot/chatbotWidgetContentController';

const PLACEHOLDER_VALUES = {
  introInput: 'Ask a question...',
  replyInput: 'Type your reply...',
  phoneInput: 'Enter your name and phone above',
  timeInput: 'Select a time or ask a question...',
  infoInput: 'Enter your information above',
  '': '',
};

let interstitialCount = 0;

/**
 * Sets the placeholder value for the user message input
 * Takes in a key from the PLACEHOLDER_VALUES constant and sets the placeholder to its respective value
 * @param placeholderKey {string}
 */
function setPlaceHolderContent(placeholderKey) {
  const inputField = getUserMsgInputField();
  inputField.placeholder = PLACEHOLDER_VALUES[placeholderKey];
}

async function handleUserMessage(message, userInformation = null) {
  renderUserMessageOnFeed(message);

  initiateSessionTimeoutCheck();

  await submitMessage(message, userInformation);
}

function initiateSessionTimeoutCheck() {
  if (!window.solvCheckForSessionTimeout) {
    window.solvCheckForSessionTimeout = setInterval(
      checkForSessionTimeout,
      5000,
    );
  }
}

async function submitMessage(message, userInformation = null) {
  interstitialCount = 0;
  showLoadingDots();
  const resData = await sendMessage(message, userInformation);

  const request_new_message = resData.request_new_message;

  hideLoadingDots();

  renderAssistantMessageOnFeed(resData.content, true);

  if (resData.user_message_content) {
    setTimeout(() => {
      renderAssistantMessageOnFeed(resData.user_message_content, false);
    }, 500);
  }

  if (request_new_message) {
    submitMessage(null);
  }

  if (resData.is_conclusion_of_conversation) {
    setPlaceHolderContent('');
    disableMessageInput();
  } else {
    const userInputField = getUserMsgInputField();
    userInputField.focus();
  }
}

async function sendMessage(message, userInformation = null) {
  const requestId = getRequestId();
  const data = {
    message,
    request_id: requestId,
    is_test: getTestInformation(),
    user_information: userInformation,
  };

  const testURLElement = document.getElementById('testing-chatbot-locally');
  let messageURL = 'https://www.solvhealth.com/dir/chatbot-widget-message';
  if (testURLElement) {
    messageURL = '/dir/chatbot-widget-message';
  }

  try {
    const res = await fetch(messageURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (res.status == 200) {
      const resData = await res.json();
      if (resData.message == 'assistant') {
        return resData;
      } else if (resData.message == 'function_call') {
        if (
          resData.content !== null &&
          resData.content !== undefined &&
          resData.content !== '' &&
          interstitialCount < 2
        ) {
          interstitialCount++;
          hideLoadingDots();
          renderAssistantMessageOnFeed(resData.content, true);
          showLoadingDots();
        }
        return sendMessage(null);
      }
    } else {
      return sendErrorMessage();
    }
  } catch (err) {
    return sendErrorMessage();
  }
}

async function sendErrorMessage(retries = 0) {
  const requestId = getRequestId();
  const data = {
    request_id: requestId,
    retries,
  };
  const testURLElement = document.getElementById('testing-chatbot-locally');
  let errorURL = 'https://www.solvhealth.com/dir/chatbot-widget-message-error';
  if (testURLElement) {
    errorURL = '/dir/chatbot-widget-message-error';
  }
  const res = await fetch(errorURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (res.status == 200) {
    const resData = await res.json();

    if (resData.message == 'waiting') {
      if (
        resData.content !== null &&
        resData.content !== undefined &&
        resData.content !== '' &&
        interstitialCount < 2
      ) {
        interstitialCount++;
        hideLoadingDots();
        renderAssistantMessageOnFeed(resData.content, true);
        showLoadingDots();
      }
      return sendErrorMessage(retries + 1);
    } else if (resData.message == 'assistant') {
      return resData;
    } else if (resData.message == 'function_call') {
      return sendMessage(null);
    }
  }
}

function renderUserMessageOnFeed(message) {
  removeButtons();
  unhideMessages();
  removeForms();
  updateBanner();

  const messageFeed = getMessageFeed();
  const newMsgWrapper = document.createElement('div');
  const newMsgContent = document.createElement('div');

  newMsgWrapper.classList.add(
    'solv-chatbot-widget__message-wrapper',
    'solv-chatbot-widget__user-message-wrapper',
  );
  newMsgWrapper.ariaLabel = 'User message';
  newMsgContent.classList.add(
    'solv-chatbot-widget__message-content',
    'solv-chatbot-widget__user-message-content',
  );
  newMsgContent.innerHTML = message;
  newMsgWrapper.appendChild(newMsgContent);

  messageFeed.appendChild(newMsgWrapper);
  autoScrollToBottom();
  deleteTimestamp();
}

function updateBanner() {
  const messageElement = getMessagesElement();
  messageElement.dataset.solvChatbotMessagesInit = 'true';
}

function unhideMessages() {
  const hiddenMessages = getHiddenMessages();
  hiddenMessages.forEach((message) => {
    message.classList.remove('solv-chatbot-widget__message-wrapper--hidden');
  });
}

function renderAssistantMessageOnFeed(message, showStamp = false) {
  // this is for the situation of the chatbot returning consecutive messages, we only want the most recent message to have a timestamp
  if (showStamp) {
    deleteTimestamp();
  }

  const messageFeed = getMessageFeed();

  const content = document.createRange().createContextualFragment(message);
  messageFeed.appendChild(content);
  autoScrollToBottom();
  if (showStamp) {
    showTimeStamp();
  }

  setUpButtons();
  setUpNewUserForm();

  if (widgetIsClosed()) {
    maximizeContainer();
  }
}

function autosizeInputField() {
  const inputField = getUserMsgInputField();

  setTimeout(() => {
    inputField.style.cssText = 'height:auto;';
    inputField.style.cssText = `height:${inputField.scrollHeight}px`;
  });
}

function setupMessageForm() {
  const userForm = getMsgInputForm();
  const sendBtn = getMsgSendBtn();
  const inputField = getUserMsgInputField();

  setPlaceHolderContent('introInput');

  userForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const messageInput = getUserMsgInputField();
    const message = messageInput.value;
    messageInput.value = '';

    await handleUserMessage(message);
  });

  inputField.addEventListener('keydown', (e) => {
    // for normal inputs, resize the container to allow for mulitiple lines
    autosizeInputField();

    // submit the form when the user presses 'enter', but not for 'shift + enter'
    if (e.which === 13 && !e.shiftKey) {
      e.preventDefault();
      userForm.requestSubmit();
    }
  });

  sendBtn.addEventListener('click', (e) => {
    userForm.requestSubmit();
  });
}

function hideSendButtonDisplay() {
  const sendBtn = getMsgSendBtn();
  sendBtn.classList.add('solv-chatbot-widget--hidden');
}

function showSendButtonDisplay() {
  const sendBtn = getMsgSendBtn();
  sendBtn.classList.remove('solv-chatbot-widget--hidden');
}

function enableMessageInput() {
  const inputField = getUserMsgInputField();
  inputField.disabled = false;
  inputField.ariaDisabled = false;
  showSendButtonDisplay();
}

function disableMessageInput() {
  const inputField = getUserMsgInputField();
  inputField.disabled = true;
  inputField.ariaDisabled = true;
  hideSendButtonDisplay();
}

function showTimeStamp() {
  const timeStamp = getTimeStampElement();
  timeStamp.classList.remove('solv-chatbot-widget--hidden');

  const initialTime = new Date().getMinutes();

  const intervalID = setInterval(() => {
    updateTimeStampValue(initialTime, intervalID);
  }, 60000);
  // NOTE: very important that this goes after interval is set
  setTimeStampIntervalID(intervalID);
}

function updateTimeStampValue(initialTime, intervalID) {
  const currentTime = new Date().getMinutes();
  const difference = currentTime - initialTime;

  if (difference > 0) {
    if (difference === 1) {
      setTimeStamp(`${difference} minute ago`, intervalID);
    } else {
      setTimeStamp(`${difference} minutes ago`, intervalID);
    }
  }
}

export {
  setupMessageForm,
  enableMessageInput,
  disableMessageInput,
  handleUserMessage,
  setPlaceHolderContent,
  initiateSessionTimeoutCheck,
};
